import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { api, HttpError } from "../api/rest";
import type { FollowSignal } from "../api/types";
import { useNow } from "../hooks/useNow";
import { usePendingSignals } from "../hooks/useSignalFeed";
import { chime } from "../lib/chime";
import { formatDollars } from "../lib/tape";
import {
  against,
  depthLabel,
  liveSignals,
  marketBadge,
  remainingMs,
  signedDollars,
  signTone,
  sideTitle,
  toneColor,
  unannounced,
  walletLabel,
} from "../lib/signals";
import { useSignals } from "../store/useSignals";
import { takeProblem } from "../lib/accounts";
import { commandTargetNow } from "../store/useAccounts";
import { useSignalSound } from "../store/useSignalSound";
import { PolymarketProfile } from "./Tier";

/**
 * Карточки сигналов копирования — единственное место интерфейса, которое видно с
 * ЛЮБОЙ страницы.
 *
 * # Почему не на экране рынка
 *
 * Окна серии закрываются каждые пять минут, и сигнал приходит по тому окну, на
 * которое оператор в этот момент почти наверняка не смотрит. Карточка, показанная
 * только открывшему нужный рынок, не показалась бы почти никогда.
 *
 * # Почему без подтверждения
 *
 * Кнопка «выставить» тратит деньги, и по идиоме проекта такое подтверждают. Здесь
 * подтверждение вредно: у карточки обратный отсчёт, и второй клик съел бы половину
 * оставшегося времени. Защиту даёт другое — сумма зафиксирована сервером
 * (FOLLOW_MAX_ORDER_USD), и промах по кнопке стоит ровно её, а не «сколько
 * угодно».
 *
 * # Почему карточка ещё и звучит
 *
 * По той же причине, по которой она висит в макете: смотреть на неё в момент
 * появления некому. Звук — единственный канал, который работает, когда вкладка
 * не на первом плане; правила звука живут в lib/chime.ts, а выключатель — в
 * шапке (SignalSoundToggle).
 */
export function SignalAlerts() {
  const { failed } = usePendingSignals();
  const signals = useSignals((s) => s.signals);
  // Секундный тик: обратный отсчёт обязан идти сам, без нового сообщения.
  // Компонент — лист дерева, поэтому перерисовка ничего не тянет за собой.
  const now = useNow(1000);

  const live = liveSignals(Object.values(signals), new Date(now));
  useChime(live);

  if (live.length === 0 && !failed) return null;

  return (
    <div className="border-b border-[var(--color-border)] bg-[var(--color-panel)]">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-2 px-4 py-2">
        {failed && (
          <p className="text-xs text-[var(--color-warn)]">
            Сигналы не загрузились: карточка могла не показаться. Это не «сигналов нет».
          </p>
        )}
        {live.map((s) => (
          <SignalCard key={s.id} signal={s} now={now} />
        ))}
      </div>
    </div>
  );
}

/**
 * Звонок на ПОЯВЛЕНИЕ карточки.
 *
 * Множество прозвучавших живёт в ref, а не в состоянии: его изменение не должно
 * вызывать перерисовку — иначе звук карточки перерисовывал бы карточку. Оно
 * растёт только живыми сигналами (единицы в час) и умирает вместе со вкладкой.
 *
 * Выключенный звук всё равно ПОМЕЧАЕТ карточки прозвучавшими: иначе включение
 * переключателя посреди жизни карточки дало бы залп по всем висящим.
 */
function useChime(live: readonly FollowSignal[]) {
  const on = useSignalSound((s) => s.on);
  const announced = useRef<Set<string>>(new Set());

  useEffect(() => {
    const fresh = unannounced(live, announced.current);
    for (const id of fresh) announced.current.add(id);
    // Один звонок на пачку: две карточки, приехавшие в один тик, — это одно
    // событие для уха, а два наложенных тона слышны как дребезг.
    if (on && fresh.length > 0) chime();
  }, [live, on]);
}

/**
 * Значок кита — единственная картинка в интерфейсе, и вот почему она тут.
 *
 * Карточка начиналась со слов «Кит поставил» цветом акцента: подпись занимала
 * восьмую часть строки, повторялась в каждой карточке и не сообщала ничего, чего
 * не сообщал бы её цвет и рамка. Место дорого — карточек на экране бывает
 * несколько, и каждая обязана уместиться в одну строку.
 *
 * Рисунок, а не эмодзи (🐋): эмодзи цветной по-своему в каждой системе и не
 * подчиняется палитре терминала, а этот значок красится `currentColor` и живёт в
 * том же акценте, что рамка карточки. Глаз залит цветом ПАНЕЛИ, а не чёрным:
 * фон карточки — панель, и чёрная точка на нём была бы дыркой.
 */
function Whale() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      className="shrink-0 text-[var(--color-accent)]"
      role="img"
    >
      <title>кит из реестра ударил по стакану</title>
      <path
        fill="currentColor"
        d="M2.6 13.5C2.6 10.2 6.2 7.6 10.6 7.6c3.8 0 7 2 7.8 4.8l4-2.6-2 3.7 2 3.7-4-2.6c-.8 2.8-4 4.8-7.8 4.8-4.4 0-8-2.6-8-5.9Z"
      />
      <circle cx="6.6" cy="12.2" r="1" fill="var(--color-panel)" />
      <path
        d="M10.8 7C10.8 4.8 12.4 3.6 14 3.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

type Echo = { text: string; tone: "ok" | "bad" | "muted" } | null;

function SignalCard({ signal, now }: { signal: FollowSignal; now: number }) {
  const [echo, setEcho] = useState<Echo>(null);
  const [busy, setBusy] = useState(false);
  const ingest = useSignals((s) => s.ingest);
  const queryClient = useQueryClient();

  const left = Math.ceil(remainingMs(signal, new Date(now)) / 1000);
  const side = signal.side === "BUY" ? "var(--color-buy)" : "var(--color-sell)";

  async function place() {
    // Сигнал берётся только аккаунтом: забирается он один раз, и когорта
    // подралась бы за него сама с собой.
    const command = commandTargetNow();
    if (!command.account) {
      setEcho({ text: takeProblem(command) ?? "выберите аккаунт", tone: "bad" });
      return;
    }
    setBusy(true);
    setEcho({ text: "отправляется…", tone: "muted" });
    try {
      await api.placeSignal(signal.id, command.account);
      setEcho({ text: "отправлено, ждём биржу…", tone: "muted" });
      // Карточка уйдёт сама сообщением сокета: сервер публикует сигнал с новым
      // исходом. Локально исход НЕ подменяем — иначе «ордер выставлен» на
      // экране означало бы «мы нажали», а не «ордер принят».
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    } catch (err) {
      // Текст берётся с сервера: «по этому окну ордер уже стоит» и «сигнал
      // сгорел» — разные ответы, и подменять их общим «не удалось» значит
      // скрыть от оператора, что именно случилось.
      setEcho({
        text: err instanceof HttpError ? err.message : "не удалось отправить",
        tone: "bad",
      });
      setBusy(false);
    }
  }

  async function skip() {
    setBusy(true);
    try {
      const res = await api.skipSignal(signal.id);
      if (res.signal) ingest(res.signal);
    } catch (err) {
      setEcho({
        text: err instanceof HttpError ? err.message : "не удалось пропустить",
        tone: "bad",
      });
      setBusy(false);
    }
  }

  const market = marketBadge(signal.slug, new Date(now));

  return (
    /*
      Ширины полей ЗАДАНЫ, а не считаются по содержимому.

      Карточек на экране обычно несколько сразу, и до правки каждая раскладывалась
      по своей длине: PnL второго кита стоял под адресом первого, цена — под
      названием рынка. Сравнить трёх китов между собой было нельзя, а именно это и
      делают, когда карточек больше одной. Числа при этом прижаты вправо: у
      столбца денег значение имеет последняя цифра, а не первая.
    */
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-[var(--color-accent)] px-3 py-2 text-xs">
      {/* Кто поставил. Значок кита вместо подписи «Кит поставил»: слово занимало
          восьмую часть строки и повторялось в каждой карточке, а значок узнаётся
          быстрее слова и не отнимает места у чисел. Смысл не потерян — он в
          подсказке и в самом значке. */}
      <span className="flex w-52 min-w-0 items-center gap-1.5">
        <Whale />
        <Link
          to={`/wallet/${signal.wallet}`}
          className="truncate font-semibold underline decoration-dotted underline-offset-2"
          // Полный адрес именно в подсказке: имя кошелёк выбирает себе сам, оно не
          // уникально, и опознавать кита по нему нельзя.
          title={`${signal.wallet} — страница кошелька`}
        >
          <span className={signal.wallet_name ? "" : "num"}>{walletLabel(signal)}</span>
        </Link>
        {/* Профиль НА БИРЖЕ рядом с нашей страницей кошелька — то же соседство,
            что в ленте сделок и у контрагентов: наша страница отвечает «как он
            торговал против нас», биржа знает его позиции по всем рынкам. Решение
            по карточке принимают за секунды, и копировать адрес руками в этот
            момент некогда. */}
        <PolymarketProfile address={signal.wallet} />
      </span>

      {/* Квалификация кита — отдельным блоком на своём фоне, а не строкой в общем
          ряду. Это ответ на вопрос «за кем мы идём», и до правки 2026-08-13 он
          лежал вперемешку со ставкой и рынком одним серым текстом. */}
      <span className="flex items-center gap-2 rounded bg-[var(--color-bg)] px-2 py-0.5">
        <span className="text-[var(--color-muted)]">PnL</span>
        <span
          className="num w-[4.5rem] text-right font-medium"
          style={{ color: toneColor(signTone(signal.whale.pnl_usd)) }}
        >
          {signedDollars(signal.whale.pnl_usd)}
        </span>
        {/* Глубина стоит РЯДОМ с PnL, а не в подсказке: своду по одним суткам
            верят иначе, чем по семи. */}
        <span className="num w-8 text-[var(--color-muted)]">{depthLabel(signal)}</span>
        {/* «На окно», а не «ставка»: величина считается по числу РЫНКОВ, и старая
            подпись обещала бы размер одного ордера. Основание — в подсказке:
            частное без знаменателя не читается. */}
        <span
          className="num w-[4.5rem] text-right"
          title={
            signal.whale.markets > 0
              ? `оборот на одно окно, посчитано по ${signal.whale.markets} окнам`
              : "основание неизвестно: сигнал записан до смены знаменателя, средняя тут по сделкам"
          }
        >
          {formatDollars(signal.whale.avg_usd)}
        </span>
        <span className="text-[var(--color-muted)]">на окно</span>
        <span className="num w-14 text-right text-[var(--color-muted)]">{signal.whale.trades}</span>
        <span className="text-[var(--color-muted)]">ставок</span>
      </span>

      {/* Рынок: актив и длительность окна — ярлыком, само окно — временем. Из
          вопроса рынка целиком («Bitcoin Up or Down - August 13, 2:30PM-2:35PM
          ET», серым) тикер приходилось вычитывать, а длительность выводить
          вычитанием времён — на карточке, живущей две минуты. Вопрос никуда не
          делся: он в подсказке. */}
      <Link
        to={`/market/${signal.condition_id}`}
        className="flex w-48 min-w-0 items-center gap-1.5 hover:text-[var(--color-accent)]"
        title={signal.question || signal.slug || signal.condition_id}
      >
        {market ? (
          <>
            <span className="rounded bg-[var(--color-bg)] px-1.5 py-0.5 font-semibold">
              {market.asset}
              <span className="num ml-1 font-normal text-[var(--color-accent)]">
                {market.duration}
              </span>
            </span>
            <span className="num text-[var(--color-muted)]">{market.window}</span>
          </>
        ) : (
          // Рынок вне серии: ни актива, ни длительности у него нет, и на их месте
          // остаётся вопрос — обрезанный, потому что ширина поля общая.
          <span className="truncate text-[var(--color-muted)] underline decoration-dotted underline-offset-2">
            {signal.question || signal.slug || "рынок"}
          </span>
        )}
      </Link>

      {/* Сама ставка: сторона задаёт цвет всему блоку — это то, что повторяет наш
          ордер, и читаться оно должно одним взглядом. Направление — стрелкой:
          «купил» и «продал» на этой дистанции различают по форме, а не по
          буквам. */}
      <span className="flex items-center gap-1.5">
        <span className="num w-3 text-center" style={{ color: side }} title={sideTitle(signal.side)}>
          {signal.side === "BUY" ? "▲" : "▼"}
        </span>
        {/* Исход НАШ, а не китовый: кнопка ставит ордер на него, и назвать здесь
            чужой значило бы описать ровно обратное тому, что произойдёт. У
            следования они совпадают, у фейда — нет, и тогда это сказано вслух:
            на бинарном рынке «против» однозначно называет и сторону кита. */}
        <span className="w-12 font-medium" style={{ color: side }}>
          {signal.outcome_name || signal.side}
        </span>
        {against(signal) && (
          <span
            className="text-[var(--color-muted)]"
            title="сценарий ставит ПРОТИВ кита: мы покупаем комплемент его исхода"
          >
            против кита
          </span>
        )}
        <span className="num w-[4.5rem] text-right" style={{ color: side }}>
          {signal.whale_price}
        </span>
        <span className="text-[var(--color-muted)]">на</span>
        <span className="num w-[4.5rem] text-right font-medium">
          {formatDollars(signal.whale_usd)}
        </span>
      </span>

      <span className="ml-auto flex items-center gap-2">
        {echo && (
          <span
            style={{
              color:
                echo.tone === "ok"
                  ? "var(--color-buy)"
                  : echo.tone === "bad"
                    ? "var(--color-sell)"
                    : "var(--color-muted)",
            }}
          >
            {echo.text}
          </span>
        )}
        {/* Обратный отсчёт стоит РЯДОМ с кнопкой: решение принимается за секунды,
            и время на него — часть этого решения. Считает он до срока РЕШЕНИЯ, а
            не до сгорания сигнала: последние 70 секунд его жизни сервер уже
            отказывает (ордер такой длины биржа гасит раньше срока), и отсчёт до
            сгорания показывал бы чужое время. Последние секунды жёлтым: карточка,
            до которой осталось десять секунд, требует другого решения, чем та, у
            которой минута. */}
        <span
          className="num"
          style={{ color: left <= 10 ? "var(--color-warn)" : "var(--color-muted)" }}
          title={
            "столько осталось НА РЕШЕНИЕ: ордер живёт дольше, но биржа гасит GTD " +
            "на минуту раньше срока, и более короткий ордер трейдер отвергнет"
          }
        >
          {left} с
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => void place()}
          className="rounded bg-[var(--color-buy)] px-3 py-1 font-medium text-black disabled:opacity-50"
        >
          выставить {formatDollars(signal.plan_usd)} по{" "}
          <span className="num">{signal.plan_price}</span>
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void skip()}
          className="rounded border border-[var(--color-border)] px-3 py-1 disabled:opacity-50"
        >
          пропустить
        </button>
      </span>
    </div>
  );
}
